import { ArrowUpIcon } from '@heroicons/react/24/outline';
import {
  forwardRef,
  type ReactNode,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Text, Textarea } from '@/shared/ui';

type ToolbarLayoutProps = {
  /** ボトムツールバー表示モード。toolbarStart などのスロットを受け付ける */
  layout: 'toolbar';
  /** textarea 上のアタッチメント（ナレッジタグ等） */
  attachments?: ReactNode;
  /** ボトムツールバーの左側（ナレッジ追加ボタン等） */
  toolbarStart?: ReactNode;
};

type EmbeddedLayoutProps = {
  /** Textarea 内に送信ボタンを重ねる従来のレイアウト。シンプルなチャット用 */
  layout?: 'embedded';
  attachments?: never;
  toolbarStart?: never;
};

type Props = {
  onSend: (message: string) => void;
  /** AI応答待ち中。入力は可能だが送信は不可 */
  waiting?: boolean;
  /** プレースホルダ。Welcome / Thread で文言を出し分けるため */
  placeholder?: string;
  /** input 全体の外側下に表示する補足文（免責事項等） */
  footer?: ReactNode;
} & (ToolbarLayoutProps | EmbeddedLayoutProps);

/**
 * 親から命令的に textarea の値を差し込み + フォーカスを当てるためのハンドル。
 * おすすめの質問カードクリック時に呼び出される（ITA-2375）。
 */
export type ChatInputHandle = {
  setMessageAndFocus: (text: string) => void;
};

/**
 * AIチャット共通の入力エリア。
 *
 * - 基本: textarea 自動拡張 / Ctrl+Enter (Cmd+Enter) 送信 / 待機中送信無効化
 * - `layout="embedded"` (デフォルト): Textarea にオーバーレイされた送信ボタン（事業チャット既存パターン）
 * - `layout="toolbar"`: Textarea 下にツールバー行を表示し、左にナレッジ操作・右に送信（Figma 汎用チャット）
 */
export const ChatInput = forwardRef<ChatInputHandle, Props>(
  ({ onSend, waiting = false, placeholder, footer, ...rest }, ref) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isEmpty = message.trim().length === 0;
    const canSend = !isEmpty && !waiting;

    useLayoutEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      if (message.length === 0) {
        el.style.height = '';
        return;
      }
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }, [message]);

    useImperativeHandle(ref, () => ({
      setMessageAndFocus: (text: string) => {
        setMessage(text);
        // setMessage 後の DOM 反映を待ってからフォーカス + キャレットを末尾へ
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          const pos = el.value.length;
          el.setSelectionRange(pos, pos);
        });
      },
    }));

    const handleSend = () => {
      if (!canSend) return;
      onSend(message.trim());
      setMessage('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSend();
      }
    };

    const sendButton = (
      <Button
        variant="primary"
        size="icon"
        onClick={handleSend}
        disabled={!canSend}
        aria-label={t('ai.chat.send-button')}
        type="button"
      >
        <ArrowUpIcon className="size-4" />
      </Button>
    );

    const isToolbar = rest.layout === 'toolbar';

    return (
      <div className="flex w-full flex-col gap-1">
        {waiting && (
          <Text variant="description" className="animate-pulse px-1">
            {t('ai.chat.waiting-response')}
          </Text>
        )}
        {isToolbar ? (
          <Card className="flex flex-col gap-2 p-2 shadow-xs focus-within:ring-2 focus-within:ring-focus-ring">
            {rest.attachments}
            <Textarea
              ref={textareaRef}
              className={`max-h-60 min-h-20 resize-none overflow-y-auto border-0 p-2 shadow-none focus-visible:ring-0 ${waiting ? 'bg-surface-strength1' : ''}`}
              size="full"
              placeholder={placeholder ?? t('ai.chat.input-placeholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="flex items-center justify-between">
              <div>{rest.toolbarStart}</div>
              {sendButton}
            </div>
          </Card>
        ) : (
          <div className="relative">
            <Textarea
              ref={textareaRef}
              className={`max-h-60 min-h-28 resize-none overflow-y-auto ${waiting ? 'bg-surface-strength1' : ''}`}
              size="full"
              placeholder={placeholder ?? t('ai.chat.input-placeholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-2 bottom-2">{sendButton}</div>
          </div>
        )}
        {footer}
      </div>
    );
  },
);

ChatInput.displayName = 'ChatInput';
